import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { ROUTES } from '@/routes/routes';
import { kstClock, kstDay } from '@/utils/fulfillmentTime';
import { toScanAscii } from '@/utils/scanAscii';
import { CameraScanField } from '../components/CameraScanField';
import { ScanIcon } from '../components/UltimateIcons';
import { asciiScanField } from '../components/asciiScanField';
import { FulfillmentSound } from '../components/outboundSound';
import { SCAN_SETTLED_MS, useScanInput } from '../components/useScanInput';
import { lookUpArrival, type ArrivalLookup } from './arrivalSortLookup';
import {
  ArrivalCache,
  WARM_DAYS,
  WARM_REFRESH_MS,
  settleWarmRound,
  warmArrivalCache,
  type WarmLoaded,
  type WarmProgress,
} from './arrivalSortCache';
import { readTodayLog, writeTodayLog } from './arrivalSortLog';
import {
  ARRIVAL_SORT_RULES,
  PARCEL_STATE_LABEL,
  SORT_CLASS_ACTION,
  SORT_CLASS_DEFINITION,
  SORT_CLASS_LABEL,
  judgeParcel,
  kstDayOf,
  recordVerdict,
  sameVerdict,
  tallyOf,
  undoVerdict,
  type LineVerdict,
  type ParcelState,
  type SortClass,
  type SortVerdict,
  type TodayScan,
} from './arrivalSortRules';
import '../wireframe.css';
import '../ultimateDesign.css';
import './arrivalSort.css';

/** Coupang's small QR hands the scanner `[V1]<barcode>`; the number after it is what the carrier printed. */
const COUPANG_QR_PREFIX = /^\[V1\]/i;
const scannedCode = (raw: string): string => toScanAscii(raw).trim().replace(COUPANG_QR_PREFIX, '');

/** The tiles across the top, in the sheet's order: the four packing groups, then the three set aside. */
const TILE_CLASSES: readonly SortClass[] = [
  'SINGLE',
  'MULTI_1',
  'MULTI_2',
  'MULTI_3',
  'WAIT',
  'HOLD',
  'CANCELLED',
  'CHECK',
];

type Shown =
  | { kind: 'idle' }
  | { kind: 'busy'; code: string }
  | { kind: 'verdict'; verdict: SortVerdict; at: string; check: Check }
  | { kind: 'other'; lookup: Exclude<ArrivalLookup, { kind: 'ORDER' }>; at: string }
  | { kind: 'error'; code: string; message: string };

/**
 * How sure the card is (rule 16): `instant` came from the warm cache and is being re-read, `confirmed` was
 * re-read and agreed, `changed` was re-read and replaced the instant one, `live` was asked of the server
 * because nothing was cached, `unconfirmed` came from the cache and the re-read failed.
 */
type Check =
  | { state: 'instant' }
  | { state: 'confirmed'; at: string }
  | { state: 'changed'; from: SortClass; at: string }
  | { state: 'live' }
  | { state: 'unconfirmed'; message: string };

/** A parcel whose verdict changed after the desk had already moved on to the next scan. */
interface ChangedNotice {
  code: string;
  from: SortClass;
  to: SortClass | null;
  at: string;
}

const PACKED: readonly SortClass[] = ['SINGLE', 'MULTI_1', 'MULTI_2', 'MULTI_3'];
const sound = (cls: SortClass) => (PACKED.includes(cls) ? FulfillmentSound.success() : FulfillmentSound.warning());

const classKey = (cls: SortClass) => cls.toLowerCase().replace('_', '');

const errorText = (err: unknown): string => {
  if (isAxiosError<{ message?: string | string[] }>(err)) {
    const message = err.response?.data?.message;
    const text = Array.isArray(message) ? message.join(', ') : message;
    return text ? `${err.response?.status ?? ''} ${text}`.trim() : err.message;
  }
  return err instanceof Error ? err.message : String(err);
};

const STATE_TONE: Readonly<Record<ParcelState, 'ok' | 'soon' | 'wait' | 'bad' | 'off'>> = {
  IN: 'ok',
  HERE: 'ok',
  SCANNED_TODAY: 'ok',
  DELIVERED: 'ok',
  OUT_FOR_DELIVERY: 'soon',
  IN_TRANSIT: 'wait',
  UNKNOWN: 'wait',
  PROBLEM: 'bad',
  NO_TRACKING: 'bad',
  NO_STOCK: 'bad',
  DELIVERED_EARLIER: 'bad',
  CANCELLED: 'off',
};

function StateChip({ state }: { state: ParcelState }) {
  return <span className={`as-state as-state-${STATE_TONE[state]}`}>{PARCEL_STATE_LABEL[state]}</span>;
}

function LineRow({ line }: { line: LineVerdict }) {
  return (
    <li>
      <StateChip state={line.state} />
      <span className="as-line-name">
        {line.name} <b>×{line.qty}</b>
      </span>
      <span className="as-line-meta">
        #{line.orderId}-{line.lineNo} · {line.sku}
        {line.isStock ? ' · stock pick' : ''}
        {line.numbers.length > 0 ? ` · parcel ${line.numbers.join(', ')}` : ''}
        {line.carrierStatus ? ` · carrier ${line.carrierStatus}` : ''}
      </span>
    </li>
  );
}

function CheckLine({ check }: { check: Check }) {
  switch (check.state) {
    case 'instant':
      return <div className="as-check as-check-wait">Judged from preloaded orders · checking with the server…</div>;
    case 'confirmed':
      return <div className="as-check as-check-ok">Server confirmed · {kstClock(check.at)}</div>;
    case 'changed':
      return (
        <div className="as-check as-check-bad" role="alert">
          Changed by the server check: was {SORT_CLASS_LABEL[check.from]} · {kstClock(check.at)}
        </div>
      );
    case 'unconfirmed':
      return (
        <div className="as-check as-check-bad" role="alert">
          Server check failed. Judged from preloaded orders only ({check.message})
        </div>
      );
    default:
      return <div className="as-check as-check-ok">Judged by server lookup</div>;
  }
}

function VerdictCard({ verdict, at, check }: { verdict: SortVerdict; at: string; check: Check }) {
  const waitingGroup = (verdict.cls === 'WAIT' || verdict.cls === 'HOLD') && verdict.group;
  const liveOrders = verdict.orders.filter((order) => !order.cancelled).length;
  return (
    <div className={`as-card as-c-${classKey(verdict.cls)}`} data-testid="arrival-verdict" data-class={verdict.cls}>
      <div className="as-card-band">
        <div className="as-card-class">{SORT_CLASS_LABEL[verdict.cls]}</div>
        <div className="as-card-action">
          {SORT_CLASS_ACTION[verdict.cls]}
          {waitingGroup && (
            <span className="as-card-then"> · when complete: {SORT_CLASS_LABEL[verdict.group as SortClass]}</span>
          )}
        </div>
        <div className="as-card-flags">
          {verdict.urgent && <span className="as-flag as-flag-urgent">Urgent</span>}
          {verdict.includesCancelled && verdict.cls !== 'CANCELLED' && (
            <span className="as-flag">Includes cancelled</span>
          )}
          {verdict.rescanOf && <span className="as-flag">Rescanned · first {kstClock(verdict.rescanOf)}</span>}
          <span className="as-flag as-flag-rule">Rule {verdict.rule}</span>
        </div>
      </div>

      <CheckLine check={check} />

      {liveOrders > 1 && verdict.contents.length > 0 && (
        <div className="as-split" role="note">
          <b>{liveOrders} orders are in this one box.</b> Pack each order separately.
        </div>
      )}

      {verdict.relocate.length > 0 && (
        <div className="as-move" role="alert">
          <b>Take these out of Wait and pack them together</b>
          <ul>
            {verdict.relocate.map((row) => (
              <li key={row.code}>
                <code>{row.code}</code> · set aside in {SORT_CLASS_LABEL[row.cls]} at {kstClock(row.firstAt)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {verdict.gatherWith.length > 0 && (
        <div className="as-gather">
          <b>Put it beside the waiting parcels of the same order</b>
          <ul>
            {verdict.gatherWith.map((row) => (
              <li key={row.code}>
                <code>{row.code}</code> · in Wait since {kstClock(row.firstAt)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <dl className="as-rows">
        <dt>Tracking No.</dt>
        <dd>
          <code className="as-code">{verdict.code}</code>
          <span className="as-muted"> · looked up {kstClock(at)}</span>
        </dd>

        <dt>Order ID</dt>
        <dd>
          <ul className="as-orders">
            {verdict.orders.map((order) => (
              <li key={order.orderId}>
                <Link to={ROUTES.fulfillment.viewOrdersOrder(order.orderId)}>#{order.orderId}</Link>
                <span className="as-muted">
                  {' '}
                  · inbounded {order.inCount}/{order.activeCount} · ordered {kstDay(order.placedAt)}
                  {order.ageDays != null ? ` (${order.ageDays} day${order.ageDays === 1 ? '' : 's'} ago)` : ''} ·{' '}
                  {order.status}
                  {order.temporaryLocation ? ` · shelf ${order.temporaryLocation}` : ''}
                </span>
                {order.holdReason && <span className="as-hold"> On hold: {order.holdReason}</span>}
                {order.cancelled && <span className="as-hold"> Cancelled</span>}
              </li>
            ))}
          </ul>
        </dd>

        <dt>In this parcel</dt>
        <dd>
          {verdict.contents.length === 0 ? (
            <span className="as-muted">No line found for this parcel</span>
          ) : (
            <ul className="as-lines">
              {verdict.contents.map((line) => (
                <LineRow key={`${line.orderId}-${line.lineNo}`} line={line} />
              ))}
            </ul>
          )}
        </dd>

        {verdict.waitingOn.length > 0 && (
          <>
            <dt>Waiting for</dt>
            <dd>
              <ul className="as-lines">
                {verdict.waitingOn.map((line) => (
                  <LineRow key={`${line.orderId}-${line.lineNo}`} line={line} />
                ))}
              </ul>
            </dd>
          </>
        )}

        <dt>Reasons / notes</dt>
        <dd>
          <ul className="as-reasons">
            {verdict.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </dd>
      </dl>
    </div>
  );
}

function OtherCard({ lookup, at }: { lookup: Exclude<ArrivalLookup, { kind: 'ORDER' }>; at: string }) {
  const body = (() => {
    switch (lookup.kind) {
      case 'INBOUND_REQUEST':
        return {
          title: 'Inbound request parcel',
          text: 'Not an ordered product but an inbound request (stock purchase or wholesale) parcel. Receive it on the Inbound Requests screen.',
          link: lookup.docId
            ? {
                to: ROUTES.fulfillment.inboundRequestDetail(lookup.docId),
                label: `Open inbound request ${lookup.inboundNo ?? lookup.docId}`,
              }
            : null,
        };
      case 'RETURN_WAYBILL':
        return {
          title: 'Return parcel',
          text: 'This number went out to a customer, so the box is a return. Receive it on the Returns screen.',
          link: { to: ROUTES.fulfillment.returns, label: 'Open Returns' },
        };
      case 'PRODUCT_BARCODE':
        return {
          title: 'Product barcode',
          text: `This is a product barcode (${lookup.productName ?? lookup.sku ?? 'no name'}). Scan the parcel label on the box.`,
          link: null,
        };
      default:
        return {
          title: 'Not found',
          text: 'This number is on no order. Open the box, check it, and register it in the unrecognized list. This screen does not register it.',
          link: { to: ROUTES.fulfillment.unrecognizedScans, label: 'Open Unrecognized Tracking List' },
        };
    }
  })();
  return (
    <div className="as-card as-c-other" data-testid="arrival-other" data-kind={lookup.kind}>
      <div className="as-card-band">
        <div className="as-card-class">{body.title}</div>
        <div className="as-card-flags">
          <span className="as-flag as-flag-rule">Rule 1</span>
        </div>
      </div>
      <dl className="as-rows">
        <dt>Tracking No.</dt>
        <dd>
          <code className="as-code">{lookup.code}</code>
          <span className="as-muted"> · looked up {kstClock(at)}</span>
        </dd>
        <dt>Next step</dt>
        <dd>
          {body.text}
          {body.link && (
            <>
              {' '}
              <Link to={body.link.to}>{body.link.label}</Link>
            </>
          )}
        </dd>
      </dl>
    </div>
  );
}

/**
 * The warm-up gauge (rule 16): fills left to right while the open orders load, and asks the desk to start
 * once they are all in. After the first round it stays full; later rounds refresh in the background.
 */
function WarmGauge({ warm, loaded }: { warm: WarmProgress | null; loaded: WarmLoaded | null }) {
  const ready = loaded?.complete === true;
  const refreshing = ready && warm != null && !warm.done;
  const listedSoFar = warm && !warm.done && warm.listed > 0 ? warm.listed : 0;
  const percent = ready
    ? 100
    : listedSoFar > 0 && warm
      ? Math.min(100, Math.round(((warm.read + warm.failed) / listedSoFar) * 100))
      : loaded
        ? 100
        : 0;
  const failed = loaded && !refreshing ? loaded.failed : (warm?.failed ?? 0);
  let text: string;
  let state = '';
  if (ready) {
    const orders = `${loaded.count.toLocaleString()} open order${loaded.count === 1 ? '' : 's'}`;
    text = `Ready to scan · ${orders} from the last ${WARM_DAYS} days loaded at ${kstClock(loaded.at)} · reloads every 10 minutes${refreshing ? ' (reloading now)' : ''}`;
    state = ' as-warm-ready';
  } else if (loaded && (warm == null || warm.done)) {
    text =
      'Could not load all the open orders. Scanning still works; a parcel that was not loaded is checked with the server (about a second)';
    state = ' as-warm-failed';
  } else if (warm == null || warm.listed === 0) {
    text = `Loading the open orders from the last ${WARM_DAYS} days…`;
  } else {
    text = `Loading open orders ${(warm.read + warm.failed).toLocaleString()} / ${warm.listed.toLocaleString()} (${percent}%)`;
  }
  return (
    <div className={`as-warm${state}`} role="status" aria-live="polite" data-testid="arrival-warm">
      <div
        className="as-warm-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label="Open orders loaded"
      >
        <div className="as-warm-fill" style={{ width: `${percent}%` }} />
      </div>
      <div className="as-warm-text">
        <b>{text}</b>
        {failed > 0 && <span> · {failed.toLocaleString()} could not be loaded (scanning them asks the server)</span>}
        {ready && loaded.reloadFailedAt && (
          <span> · the reload at {kstClock(loaded.reloadFailedAt)} failed, still using the earlier load</span>
        )}
      </div>
      {!loaded && (
        <div className="as-warm-hint">
          Please start scanning once all orders are loaded. A parcel scanned before that is checked with the server and
          takes about a second.
        </div>
      )}
      <div className="as-warm-note">This screen only reads: it never changes inbound, location or status.</div>
    </div>
  );
}

/**
 * Fulfillment › Arrival Sort (SSD-7326). DY's Arrived_Tracking_Sort sheet as a desk screen: scan a parcel
 * at the door, read where it goes. Every scan reads the order live, the verdict cites its numbered rule,
 * and the rules are printed under the screen. Nothing here writes to the server.
 */
export default function FulfillmentArrivalSortPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const scanInput = useScanInput({ preventScroll: true });
  const [code, setCode] = useState('');
  const [shown, setShown] = useState<Shown>({ kind: 'idle' });
  const [log, setLog] = useState<TodayScan[]>(() => readTodayLog(new Date().toISOString()));
  const [filter, setFilter] = useState<SortClass | null>(null);
  const [clearArmed, setClearArmed] = useState(false);
  const logRef = useRef(log);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const lastSubmit = useRef<{ code: string; at: number } | null>(null);
  const typedSinceSubmit = useRef(false);
  const cache = useRef(new ArrivalCache());
  // Codes whose lookup is still running, counted, so the same code scanned twice stays pending until both end.
  const pending = useRef(new Map<string, number>());
  const recheck = useRef<Promise<void>>(Promise.resolve());
  const seq = useRef(0);
  const [warm, setWarm] = useState<WarmProgress | null>(null);
  // The last finished round; once set, later rounds refresh in the background and the desk stays ready.
  const [loaded, setLoaded] = useState<WarmLoaded | null>(null);
  const [notices, setNotices] = useState<ChangedNotice[]>([]);

  // Rule 16: read the recent open orders once on opening and again every WARM_REFRESH_MS, so a scan of a
  // known parcel is judged from memory. A failed or slow warm-up only means scans ask the server.
  useEffect(() => {
    const signal = { aborted: false };
    let running = false;
    const run = () => {
      if (running) {
        return;
      }
      running = true;
      void warmArrivalCache(client, cache.current, {
        nowIso: new Date().toISOString(),
        signal,
        onProgress: (progress) => {
          if (signal.aborted) {
            return;
          }
          setWarm(progress);
          if (progress.done) {
            const at = new Date().toISOString();
            setLoaded((previous) => settleWarmRound(previous, progress, at));
          }
        },
      }).finally(() => {
        running = false;
      });
    };
    run();
    const timer = window.setInterval(run, WARM_REFRESH_MS);
    return () => {
      signal.aborted = true;
      window.clearInterval(timer);
    };
  }, [client]);

  useEffect(() => {
    scanInput.reclaim();
  }, [scanInput]);

  const commitLog = useCallback((next: TodayScan[], nowIso: string) => {
    logRef.current = next;
    setLog(next);
    writeTodayLog(nowIso, next);
  }, []);

  /** Today's log as of now: a desk left open past midnight starts the new day empty (rule 14). */
  const todayLog = (nowIso: string): TodayScan[] => {
    const today = kstDayOf(nowIso);
    return logRef.current.filter((scan) => kstDayOf(scan.lastAt) === today);
  };

  const judge = useCallback(
    (scanned: string) => {
      const key = scanned.trim().toUpperCase();
      const token = ++seq.current;
      const local = cache.current.bundle(scanned);
      pending.current.set(key, (pending.current.get(key) ?? 0) + 1);
      const release = () => {
        const left = (pending.current.get(key) ?? 1) - 1;
        if (left > 0) {
          pending.current.set(key, left);
        } else {
          pending.current.delete(key);
        }
      };

      if (local) {
        // Rule 16: answer from memory at once, then re-read the bundle and say so if the server disagrees.
        const nowIso = new Date().toISOString();
        const base = todayLog(nowIso);
        const previousRow = base.find((scan) => scan.code.toUpperCase() === key) ?? null;
        const alsoScanned = [...pending.current.keys()];
        const instant = judgeParcel({
          code: scanned,
          details: local.details,
          log: base,
          nowIso,
          truncated: local.truncated,
          alsoScanned,
        });
        commitLog(recordVerdict(base, instant, nowIso), nowIso);
        setShown({ kind: 'verdict', verdict: instant, at: nowIso, check: { state: 'instant' } });
        sound(instant.cls);
        scanInput.reclaim();
        // The server re-reads the bundle behind the card; only a disagreement makes a second sound.
        recheck.current = recheck.current.then(() =>
          lookUpArrival(client, scanned)
            .then((lookup) => {
              const checkedAt = new Date().toISOString();
              const stillShown = token === seq.current;
              if (lookup.kind !== 'ORDER') {
                commitLog(undoVerdict(logRef.current, instant, nowIso, previousRow), checkedAt);
                if (stillShown) {
                  setShown({ kind: 'other', lookup, at: checkedAt });
                } else {
                  setNotices((list) =>
                    [{ code: scanned, from: instant.cls, to: null, at: checkedAt }, ...list].slice(0, 5),
                  );
                }
                FulfillmentSound.failure();
                return;
              }
              lookup.details.forEach((detail) => cache.current.put(detail));
              // Judged against the same log as the instant answer, so only the server's data can change the
              // verdict; a sibling scanned since is judged on its own scan, with this one already in the log.
              const confirmed = judgeParcel({
                code: scanned,
                details: lookup.details,
                log: base,
                nowIso,
                truncated: lookup.truncated,
                alsoScanned,
              });
              if (sameVerdict(instant, confirmed)) {
                if (stillShown) {
                  setShown({
                    kind: 'verdict',
                    verdict: instant,
                    at: nowIso,
                    check: { state: 'confirmed', at: checkedAt },
                  });
                }
                return;
              }
              commitLog(
                recordVerdict(undoVerdict(logRef.current, instant, nowIso, previousRow), confirmed, nowIso),
                checkedAt,
              );
              FulfillmentSound.failure();
              if (stillShown) {
                setShown({
                  kind: 'verdict',
                  verdict: confirmed,
                  at: nowIso,
                  check: { state: 'changed', from: instant.cls, at: checkedAt },
                });
              } else {
                setNotices((list) =>
                  [{ code: scanned, from: instant.cls, to: confirmed.cls, at: checkedAt }, ...list].slice(0, 5),
                );
              }
            })
            .catch((err: unknown) => {
              FulfillmentSound.warning();
              if (token === seq.current) {
                setShown({
                  kind: 'verdict',
                  verdict: instant,
                  at: nowIso,
                  check: { state: 'unconfirmed', message: errorText(err) },
                });
              }
            })
            .finally(release),
        );
        return;
      }

      setShown({ kind: 'busy', code: scanned });
      // Not cached: ask the server, one at a time in the order scanned, so each verdict reads the log the
      // previous one wrote.
      queue.current = queue.current.then(async () => {
        try {
          const lookup = await lookUpArrival(client, scanned);
          const nowIso = new Date().toISOString();
          const current = todayLog(nowIso);
          if (lookup.kind !== 'ORDER') {
            if (token === seq.current) {
              setShown({ kind: 'other', lookup, at: nowIso });
            }
            FulfillmentSound.warning();
            return;
          }
          lookup.details.forEach((detail) => cache.current.put(detail));
          const verdict = judgeParcel({
            code: scanned,
            details: lookup.details,
            log: current,
            nowIso,
            truncated: lookup.truncated,
            alsoScanned: [...pending.current.keys()],
          });
          commitLog(recordVerdict(current, verdict, nowIso), nowIso);
          if (token === seq.current) {
            setShown({ kind: 'verdict', verdict, at: nowIso, check: { state: 'live' } });
          }
          sound(verdict.cls);
        } catch (err) {
          if (token === seq.current) {
            setShown({ kind: 'error', code: scanned, message: errorText(err) });
          }
          FulfillmentSound.failure();
        } finally {
          release();
          scanInput.reclaim();
        }
      });
    },
    // todayLog reads a ref, so it needs no dependency of its own.
    [client, commitLog, scanInput],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const scanned = scannedCode(code);
    if (!scanned) {
      return;
    }
    // A repeat of the untouched code straight after is the scanner's second line terminator, not a second parcel.
    const now = Date.now();
    const last = lastSubmit.current;
    if (last && last.code === scanned && !typedSinceSubmit.current && now - last.at < SCAN_SETTLED_MS) {
      scanInput.settle();
      return;
    }
    lastSubmit.current = { code: scanned, at: now };
    typedSinceSubmit.current = false;
    scanInput.settle();
    judge(scanned);
  };

  const tally = useMemo(() => tallyOf(log), [log]);
  const toPull = useMemo(() => log.filter((scan) => scan.pulledAt).length, [log]);
  const rows = useMemo(
    () =>
      [...log]
        .filter((scan) => !filter || (scan.pulledInto ?? scan.cls) === filter)
        .sort((a, b) => b.lastAt.localeCompare(a.lastAt)),
    [log, filter],
  );

  const clearToday = () => {
    if (!clearArmed) {
      setClearArmed(true);
      window.setTimeout(() => setClearArmed(false), 4000);
      return;
    }
    setClearArmed(false);
    commitLog([], new Date().toISOString());
    setFilter(null);
  };

  return (
    <div className="wms-wireframe ff-ultimate wms-arrival-sort">
      <div className="titlerow">
        <div>
          <h2>WMS - Arrival Sort</h2>
          <p className="sub">Arrived parcels · tracking sort</p>
        </div>
      </div>
      <WarmGauge warm={warm} loaded={loaded} />

      <div className="as-top">
        <section className="as-left">
          <form className="as-scan" onSubmit={submit}>
            <label className="as-scan-label" htmlFor="arrival-sort-code">
              Tracking No. <span>Scan / type here</span>
            </label>
            <div className="as-scan-row">
              <div className="as-scan-box">
                <ScanIcon className="as-scan-icon" />
                <CameraScanField label="Scan tracking number with camera" className="as-scan-field">
                  <input
                    id="arrival-sort-code"
                    ref={scanInput.ref}
                    aria-label="Tracking No"
                    {...asciiScanField((value) => {
                      scanInput.noteTyping();
                      typedSinceSubmit.current = true;
                      setCode(value);
                    })}
                    value={code}
                    onClick={scanInput.reclaim}
                    placeholder="Scan the parcel barcode"
                    // Password managers ignore autoComplete="off"; their pop-up would swallow the scanner's keystrokes.
                    data-1p-ignore
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </CameraScanField>
              </div>
              {/* Never disabled: a disabled default button makes the browser swallow the scanner's Enter. */}
              <button type="submit" className="btn btn-blue">
                Sort
              </button>
            </div>
          </form>

          {notices.length > 0 && (
            <div className="as-notices" role="alert">
              {notices.map((notice) => (
                <div key={`${notice.code}-${notice.at}`} className="as-notice">
                  <span>
                    The verdict for <code>{notice.code}</code> changed after the server check:{' '}
                    <b>
                      {SORT_CLASS_LABEL[notice.from]} →{' '}
                      {notice.to ? SORT_CLASS_LABEL[notice.to] : 'not an order parcel'}
                    </b>{' '}
                    · {kstClock(notice.at)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-line"
                    onClick={() => setNotices((list) => list.filter((row) => row !== notice))}
                  >
                    OK
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="as-result" aria-live="polite">
            {shown.kind === 'idle' && <div className="as-empty">Scan a parcel to see its class and reasons here.</div>}
            {shown.kind === 'busy' && (
              <div className="as-empty">
                <code>{shown.code}</code> looking up…
              </div>
            )}
            {shown.kind === 'verdict' && <VerdictCard verdict={shown.verdict} at={shown.at} check={shown.check} />}
            {shown.kind === 'other' && <OtherCard lookup={shown.lookup} at={shown.at} />}
            {shown.kind === 'error' && (
              <div className="as-card as-c-hold" role="alert" data-testid="arrival-error">
                <div className="as-card-band">
                  <div className="as-card-class">Lookup failed</div>
                  <div className="as-card-action">Could not sort. Scan again.</div>
                </div>
                <dl className="as-rows">
                  <dt>Tracking No.</dt>
                  <dd>
                    <code className="as-code">{shown.code}</code>
                  </dd>
                  <dt>Error</dt>
                  <dd>{shown.message}</dd>
                </dl>
              </div>
            )}
          </div>
        </section>

        <aside className="as-right">
          <div className="as-tiles">
            {TILE_CLASSES.map((cls) => (
              <button
                key={cls}
                type="button"
                className={`as-tile as-c-${classKey(cls)}${filter === cls ? ' on' : ''}`}
                onClick={() => setFilter(filter === cls ? null : cls)}
                aria-pressed={filter === cls}
                title={SORT_CLASS_DEFINITION[cls].meaning}
              >
                <span className="as-tile-label">{SORT_CLASS_LABEL[cls]}</span>
                <span className="as-tile-num num">{tally[cls]}</span>
                <span className="as-tile-unit">parcels</span>
              </button>
            ))}
          </div>
          <div className="as-total">
            <span>
              Scanned today <b className="num">{tally.total}</b>
              {toPull > 0 && (
                <>
                  {' '}
                  · taken out of Wait <b className="num">{toPull}</b>
                </>
              )}
            </span>
            <button
              type="button"
              className={`btn btn-sm ${clearArmed ? 'btn-red-line' : 'btn-line'}`}
              onClick={clearToday}
            >
              {clearArmed ? 'Click again to clear' : 'Clear log'}
            </button>
          </div>
          <p className="as-note">
            Counts the parcels scanned on this PC today and starts over at midnight KST. Scans on other PCs do not show
            here, so sort on one PC. Click a tile to see only that class below.
          </p>
        </aside>
      </div>

      <section className="sec">
        {/* The button sits beside the heading, not inside it, so the heading's name stays "Scanned today". */}
        <div className="psub">
          <h3>Scanned today</h3>
          <span className="sp" />
          {filter && (
            <button type="button" className="btn btn-sm btn-line" onClick={() => setFilter(null)}>
              {SORT_CLASS_LABEL[filter]} only · show all
            </button>
          )}
        </div>
        <div className="as-table-wrap">
          <table className="tbl as-table" aria-label="Today's arrival scans">
            <colgroup>
              <col className="as-col-time" />
              <col className="as-col-code" />
              <col className="as-col-class" />
              <col className="as-col-group" />
              <col className="as-col-orders" />
              <col className="as-col-units" />
              <col className="as-col-pulled" />
            </colgroup>
            <thead>
              <tr>
                <th>Time</th>
                <th>Tracking No.</th>
                <th>Class</th>
                <th>Group when complete</th>
                <th>Order ID</th>
                <th>Units</th>
                <th>Taken out of Wait</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="as-muted">
                    {filter ? 'No parcels in this class yet.' : 'No parcels scanned today yet.'}
                  </td>
                </tr>
              ) : (
                rows.map((scan) => (
                  <tr key={scan.code}>
                    <td className="num">{kstClock(scan.lastAt)}</td>
                    <td>
                      <code>{scan.code}</code>
                    </td>
                    <td>
                      <span className={`as-pill as-c-${classKey(scan.cls)}`}>{SORT_CLASS_LABEL[scan.cls]}</span>
                    </td>
                    <td>{scan.group ? SORT_CLASS_LABEL[scan.group] : '—'}</td>
                    <td>
                      {scan.orderIds.map((orderId, index) => (
                        <span key={orderId}>
                          {index > 0 ? ', ' : ''}
                          <Link to={ROUTES.fulfillment.viewOrdersOrder(orderId)}>#{orderId}</Link>
                        </span>
                      ))}
                    </td>
                    <td className="num">{scan.units}</td>
                    <td>
                      {scan.pulledAt && scan.pulledInto
                        ? `${kstClock(scan.pulledAt)} → ${SORT_CLASS_LABEL[scan.pulledInto]}`
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <details className="sec as-rules as-defs">
        <summary>The 8 classes: what each means and where the parcel goes</summary>
        <dl className="as-def-list">
          {TILE_CLASSES.map((cls) => (
            <div key={cls} className="as-def-row">
              <dt>
                <span className={`as-pill as-c-${classKey(cls)}`}>{SORT_CLASS_LABEL[cls]}</span>
              </dt>
              <dd>
                <span className="as-def-action">{SORT_CLASS_ACTION[cls]}</span>
                <span>{SORT_CLASS_DEFINITION[cls].meaning}</span>
                <span className="as-muted">
                  {' '}
                  (rule{SORT_CLASS_DEFINITION[cls].rules.length > 1 ? 's' : ''}{' '}
                  {SORT_CLASS_DEFINITION[cls].rules.join(', ')})
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </details>

      <details className="sec as-rules">
        <summary>{`The ${ARRIVAL_SORT_RULES.length} sorting rules: the "Rule N" on a result card is this number`}</summary>
        <ol>
          {ARRIVAL_SORT_RULES.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ol>
        <p className="as-note">
          {`Follows Dongyun's Arrived_Tracking_Sort sheet and his 10/8 flow (hold → single → multi → wait). Where the sheet chose Multi by whether a parcel number was registered, this screen asks whether the rest of the order arrives today.`}
        </p>
      </details>
    </div>
  );
}
